import type {Box2Like, Size2Like, Vector2Like} from "@cm/math"
import {Box2, Size2, Vector2} from "@cm/math"
import type {ClearArgs, DrawArgs, HalImageStats, HalImageView, ImageViewArgs, ImageViewType, ReadImageDataArgs, WriteImageDataArgs} from "@cm/hal"
import {getMipLevelSize} from "@cm/hal"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import type {WebGl2ImageDescriptor} from "#webgl2/webgl2-image"
import {WebGl2ImageBase} from "#webgl2/webgl2-image-base"
import {assertNever} from "@cm/utils"
import {getMipLevelBiasScale, isHalImageVirtual} from "@cm/hal/hal-image/utils"
import type {ViewTransformArgs} from "@cm/hal/hal-paintable"
import {ALLOW_WRAPPING_VIEWS} from "#webgl2/constants"

export class WebGl2ImageView extends WebGl2ImageBase implements HalImageView {
    readonly isHalImageView = true

    readonly context: WebGl2Context

    readonly type: ImageViewType

    constructor(
        readonly image: WebGl2ImageBase,
        {type, region, mipLevelOffset}: ImageViewArgs,
    ) {
        super()
        if (!ALLOW_WRAPPING_VIEWS && type !== "sub-image") {
            throw new Error("Wrapping views are disabled.")
        }
        if (image instanceof WebGl2ImageView) {
            if (image.type !== "sub-image" && type !== "sub-image") {
                throw new Error(
                    `Can only create a sub-image view of a wrapped or dilated sub-image image. Source image view type: ${image.type}, requested view type: ${type}`,
                )
            }
        }
        this.context = image.context
        this.type = type
        this.mipLevelOffset = mipLevelOffset ?? 0
        this.mipLevelOffsetScale = Math.pow(2, this.mipLevelOffset)
        const imageSize = this.image.getMipLevelSize(this.mipLevelOffset)
        this.region = region ?? {x: 0, y: 0, width: imageSize.width, height: imageSize.height}
        if (this.region.width <= 0 || this.region.height <= 0) {
            throw new Error("Region width and height must be greater than 0")
        }
        if (isHalImageVirtual(this.image)) {
            this.noWriteAllowedDueToBugs = true // TODO remove this once bugs are fixed
        }
        switch (this.type) {
            case "sub-image":
                if (
                    this.region.x < 0 ||
                    this.region.y < 0 ||
                    this.region.x + this.region.width > imageSize.width ||
                    this.region.y + this.region.height > imageSize.height
                ) {
                    throw new Error("Region must be within the bounds of the mip level size")
                }
                if (this.image instanceof WebGl2ImageView) {
                    if (this.image.type === "dilated-wrapped-sub-image") {
                        this.noWriteAllowedDueToBugs = true // TODO remove this once bugs are fixed
                    }
                }
                this.regionWrappingOffset = new Vector2(0, 0)
                this.regionAndOffsets = [{viewport: Box2.fromBox2Like(this.region), transformOffset: Vector2.fromVector2Like(this.region)}]
                this.wrapOffset = Vector2.fromVector2Like(this.image.descriptor.wrapOffset)
                this.wrapSize = Size2.fromSize2Like(this.image.descriptor.wrapSize)
                this.wrapGap = Size2.fromSize2Like(this.image.descriptor.wrapGap)
                break
            case "wrapped-sub-image":
            case "dilated-wrapped-sub-image": {
                if (this.type === "wrapped-sub-image" && (this.region.width > imageSize.width || this.region.height > imageSize.height)) {
                    throw new Error("Region must not be larger than the mip level size")
                }
                const regionSizeDifference = Size2.sub(this.region, imageSize)
                this.wrapOffset = Vector2.fromVector2Like(this.image.descriptor.textureOffset)
                this.wrapSize = imageSize
                this.wrapGap = Size2.max(new Size2(0, 0), regionSizeDifference)

                // const wrappedRegionOffset = wrapOffset(this.region, imageSize)
                const wrapGapVector = Vector2.fromSize2Like(this.wrapGap)
                const wrappedRegionOffset = Vector2.sub(
                    Vector2.wrap(Vector2.add(this.region, wrapGapVector), Size2.add(imageSize, this.wrapGap)),
                    wrapGapVector,
                )
                // this.regionWrappingOffset = Vector2.sub(this.region, wrappedRegionOffset)
                this.regionWrappingOffset = new Vector2(0, 0)
                const wrappedRegion = {
                    ...wrappedRegionOffset,
                    width: this.region.width,
                    height: this.region.height,
                }

                this.regionAndOffsets = [
                    {
                        viewport: {
                            x: wrappedRegion.x,
                            y: wrappedRegion.y,
                            width: Math.min(wrappedRegion.width, imageSize.width - wrappedRegion.x),
                            height: Math.min(wrappedRegion.height, imageSize.height - wrappedRegion.y),
                        },
                        transformOffset: {
                            x: wrappedRegion.x,
                            y: wrappedRegion.y,
                        },
                    },
                ]
                if (wrappedRegion.x + wrappedRegion.width > imageSize.width) {
                    this.regionAndOffsets.push({
                        viewport: {
                            x: 0,
                            y: wrappedRegion.y,
                            width: wrappedRegion.x + wrappedRegion.width - imageSize.width - this.wrapGap.width,
                            height: Math.min(wrappedRegion.height, imageSize.height - wrappedRegion.y),
                        },
                        transformOffset: {
                            x: wrappedRegion.x - imageSize.width - this.wrapGap.width,
                            y: wrappedRegion.y,
                        },
                    })
                }
                if (wrappedRegion.y + wrappedRegion.height > imageSize.height) {
                    this.regionAndOffsets.push({
                        viewport: {
                            x: wrappedRegion.x,
                            y: 0,
                            width: Math.min(wrappedRegion.width, imageSize.width - wrappedRegion.x),
                            height: wrappedRegion.y + wrappedRegion.height - imageSize.height - this.wrapGap.height,
                        },
                        transformOffset: {
                            x: wrappedRegion.x,
                            y: wrappedRegion.y - imageSize.height - this.wrapGap.height,
                        },
                    })
                }
                if (wrappedRegion.x + wrappedRegion.width > imageSize.width && wrappedRegion.y + wrappedRegion.height > imageSize.height) {
                    this.regionAndOffsets.push({
                        viewport: {
                            x: 0,
                            y: 0,
                            width: wrappedRegion.x + wrappedRegion.width - imageSize.width - this.wrapGap.width,
                            height: wrappedRegion.y + wrappedRegion.height - imageSize.height - this.wrapGap.height,
                        },
                        transformOffset: {
                            x: wrappedRegion.x - imageSize.width - this.wrapGap.width,
                            y: wrappedRegion.y - imageSize.height - this.wrapGap.height,
                        },
                    })
                }
                break
            }
            default:
                assertNever(this.type)
        }
    }

    get descriptor(): WebGl2ImageDescriptor {
        const sourceDescriptor = this.image.descriptor
        return {
            ...this.image.descriptor,
            width: this.region.width, // TODO should this be the base level size (considering the mip level offset) ?
            height: this.region.height,
            numMipLevels: sourceDescriptor.numMipLevels - this.mipLevelOffset,
            textureOffset: Vector2.add(sourceDescriptor.textureOffset, this.region),
            wrapOffset: this.wrapOffset,
            wrapSize: this.wrapSize,
            wrapGap: this.wrapGap,
            mipLevelOffset: sourceDescriptor.mipLevelOffset + this.mipLevelOffset,
        }
    }

    get stats(): HalImageStats {
        return this.image.stats
    }

    get forceAlphaToOne(): boolean {
        return this.image.forceAlphaToOne
    }

    dispose(): void {
        this.image.dispose()
    }

    // HalPaintable
    beginDraw(args: DrawArgs): number {
        if (this.noWriteAllowedDueToBugs) {
            throw new Error("Writing to this view is not allowed due to known bugs. Those need to be fixed first.")
        }
        let numPasses = 0
        this.regionAndOffsets.forEach((regionAndOffset) => (numPasses += this.image.beginDraw(this.adjustDrawArgs(args, regionAndOffset))))
        return numPasses
    }

    // HalPaintable
    beginDrawPass(args: DrawArgs, pass: number): ViewTransformArgs {
        const drawPass = Math.floor(pass / this.regionAndOffsets.length)
        const regionIndex = pass % this.regionAndOffsets.length
        const regionAndOffset = this.regionAndOffsets[regionIndex]
        const adjustedDrawArgs = this.adjustDrawArgs(args, regionAndOffset)
        return this.image.beginDrawPass(adjustedDrawArgs, drawPass)
    }

    // HalPaintable
    endDrawPass(args: DrawArgs, pass: number): void {
        const drawPass = Math.floor(pass / this.regionAndOffsets.length)
        const regionIndex = pass % this.regionAndOffsets.length
        const regionAndOffset = this.regionAndOffsets[regionIndex]
        const adjustedDrawArgs = this.adjustDrawArgs(args, regionAndOffset)
        this.image.endDrawPass(adjustedDrawArgs, drawPass)
        if (regionIndex === this.regionAndOffsets.length - 1) {
            // last region, end the (sub) draw
            this.image.endDraw(adjustedDrawArgs)
        }
    }

    // HalPaintable
    endDraw(args: DrawArgs): void {}

    private adjustDrawArgs(args: DrawArgs, regionAndOffset: RegionAndOffset): DrawArgs {
        const mipLevelScale = getMipLevelBiasScale(args.mipLevel)
        const clipRegion = Box2.fromBox2Like(args.clipRegion).mulInPlace(1 / mipLevelScale)
        const sourceSpaceRegion = {
            x: regionAndOffset.viewport.x - regionAndOffset.transformOffset.x,
            y: regionAndOffset.viewport.y - regionAndOffset.transformOffset.y,
            width: regionAndOffset.viewport.width,
            height: regionAndOffset.viewport.height,
        }
        const intersectedSourceSpaceRegion = Box2.intersect(sourceSpaceRegion, clipRegion)
        // const offsetShift = Vector2.sub(clipRegion, sourceSpaceRegion)
        const offsetShift = Vector2.max(new Vector2(0, 0), Vector2.sub(clipRegion, sourceSpaceRegion))
        // const offsetShift = Vector2.sub(intersectedSourceSpaceRegion, sourceSpaceRegion) //, clipRegion)
        // const offsetShift = Vector2.max(new Vector2(0, 0), Vector2.sub(sourceSpaceRegion, clipRegion))
        // const adjustedTransformOffset = Vector2.add(args.offset, regionAndOffset.offset) //.addInPlace(offsetShift)
        const adjustedTransformOffset = Vector2.add(args.transformOffset, regionAndOffset.transformOffset)
        // adjustedTransformOffset.x = wrap(adjustedTransformOffset.x, this.wrapSize.width)
        // adjustedTransformOffset.y = wrap(adjustedTransformOffset.y, this.wrapSize.height)
        let adjustedClipRegion = Box2.fromPositionAndSize(
            Vector2.add(intersectedSourceSpaceRegion, regionAndOffset.transformOffset),
            intersectedSourceSpaceRegion,
        )

        adjustedTransformOffset.mulInPlace(mipLevelScale).floorInPlace()
        adjustedClipRegion = Box2.expandToIntegers(adjustedClipRegion.mulInPlace(mipLevelScale))

        // const adjustedClipRegion = Box2.fromPositionAndSize(
        //     Vector2.add(clipRegion, regionAndOffset.region),
        //     Size2.min(clipRegion, regionAndOffset.region),
        // )
        // const adjustedTransformOffset = Vector2.add(args.offset, regionAndOffset.offset)
        const adjustedUvTransformOffset = Vector2.add(args.uvTransformOffset, this.regionWrappingOffset)
        const adjustedMipLevel = this.adjustMipLevel(args.mipLevel)
        return {
            ...args,
            clipRegion: adjustedClipRegion,
            transformOffset: adjustedTransformOffset,
            uvTransformOffset: adjustedUvTransformOffset,
            mipLevel: adjustedMipLevel,
        }
    }

    // HalPaintable
    get width(): number {
        return this.descriptor.width
    }

    // HalPaintable
    get height(): number {
        return this.descriptor.height
    }

    // HalPaintable
    clear(args?: ClearArgs) {
        const mipLevel = args?.mipLevel ?? 0
        const region = args?.region ?? {x: 0, y: 0, ...this.getMipLevelSize(mipLevel)}
        this.image.clear({
            color: args?.color,
            region: this.adjustRegion(region),
            mipLevel: this.adjustMipLevel(mipLevel),
        })
    }

    getMipLevelSize(mipLevel: number): Size2Like {
        return getMipLevelSize(this.region, mipLevel)
    }

    readImageDataFloat(args?: ReadImageDataArgs): Float32Array {
        const mipLevel = args?.mipLevel ?? 0
        const region = args?.region ?? {x: 0, y: 0, ...this.getMipLevelSize(mipLevel)}
        return this.image.readImageDataFloat({
            region: this.adjustRegion(region),
            mipLevel: this.adjustMipLevel(mipLevel),
        })
    }

    writeImageData(args: WriteImageDataArgs): Promise<void> {
        return this.image.writeImageData({
            ...args,
            targetOffset: this.adjustOffset(args.targetOffset, true),
            targetMipLevel: this.adjustMipLevel(args.targetMipLevel ?? 0),
        })
    }

    prepareForUseAsSourceImage() {
        this.image.prepareForUseAsSourceImage()
    }

    private adjustMipLevel(mipLevel: number): number {
        return mipLevel + this.mipLevelOffset
    }

    private adjustRegion(region: Box2Like): Box2Like {
        // region ??= {x: 0, y: 0, width: this.region.width, height: this.region.height}
        // if (region.x >= this.region.width || region.y >= this.region.height) {
        //     throw new Error("Invalid region")
        // }
        return {
            ...this.adjustOffset(region),
            width: region.width,
            height: region.height,
        }
    }

    private adjustOffset(offset?: Vector2Like, wrapResult?: boolean): Vector2 {
        offset ??= {x: 0, y: 0}
        const adjustedOffset = Vector2.add(offset, Vector2.mul(this.region, this.mipLevelOffsetScale))
        // if (wrapResult) {
        //     // wrap the offset to support wrapped views
        //     adjustedOffset.x = wrap(adjustedOffset.x, this.descriptor.width)
        //     adjustedOffset.y = wrap(adjustedOffset.y, this.descriptor.height)
        // }
        return adjustedOffset
    }

    private regionWrappingOffset: Vector2Like
    private region: Box2Like
    private mipLevelOffset: number
    private mipLevelOffsetScale: number
    private wrapOffset: Vector2Like
    private wrapSize: Size2Like
    private wrapGap: Size2Like
    private regionAndOffsets: RegionAndOffset[]

    private noWriteAllowedDueToBugs = false // TODO remove this once bugs are fixed
}

type RegionAndOffset = {
    viewport: Box2Like
    transformOffset: Vector2Like
}
